import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type Estimate = components['schemas']['EstimateResponse'];
export type EstimateSummary = components['schemas']['EstimateSummaryResponse'];
export type EstimateLine = components['schemas']['LineItemResponse'];
export type EstimatePreview = components['schemas']['EstimatePreviewResponse'];
export type SaveLine = components['schemas']['SaveLineItemRequest'];
export type CatalogItem = components['schemas']['CatalogItemResponse'];

/** The statuses M2-05 derives, in the order the list's filter offers them. */
export const ESTIMATE_STATUSES = [
  'Draft',
  'Sent',
  'Approved',
  'Declined',
  'Expired',
  'Superseded',
] as const;

const ESTIMATES_KEY = ['estimates'] as const;

/**
 * The API refused an estimate act and said why (409): a sent estimate cannot
 * be edited, an expired one cannot be approved, an approved one cannot be
 * revised. The sentence is the part the office can act on, so it is carried
 * verbatim, like `VisitRefusedError`.
 */
export class EstimateRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EstimateRefusedError';
  }
}

/** A 400 the API explains, with its own sentence per field. */
export class EstimateInvalidError extends Error {
  constructor(public readonly errors: Record<string, string[]>) {
    super(Object.values(errors).flat()[0] ?? 'Invalid');
    this.name = 'EstimateInvalidError';
  }
}

/**
 * ⚠️ Read from openapi-fetch's `error`, never by re-reading the Response — its
 * body is already consumed. A 409 carries a JSON string; a 400 a
 * ValidationProblemDetails.
 */
function failure(error: unknown, response: Response, what: string): Error {
  if (response.status === 409) {
    return new EstimateRefusedError(typeof error === 'string' ? error : `${what} refused`);
  }
  if (response.status === 400) {
    const problem = error as { errors?: Record<string, string[]> } | undefined;
    return new EstimateInvalidError(problem?.errors ?? {});
  }
  return new Error(`${what} failed: ${response.status}`);
}

export interface EstimateFilters {
  status?: string;
  customerId?: string;
  jobId?: string;
}

/**
 * A list of estimates (M2-05), newest number first. A caller without
 * `customers.read` gets only those on jobs they are on — the API narrows it
 * (M2-06), so this never filters.
 */
export function useEstimates(filters: EstimateFilters, enabled = true) {
  const query = {
    status: filters.status || undefined,
    customerId: filters.customerId || undefined,
    jobId: filters.jobId || undefined,
  };
  return useQuery({
    enabled,
    queryKey: [...ESTIMATES_KEY, 'list', query] as const,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/estimates', { params: { query } });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/estimates failed: ${response.status}`);
      }
      return data;
    },
  });
}

/** Thrown for a 403 on one estimate: it is on a job the caller is not on (M2-06). */
export class EstimateForbiddenError extends Error {
  constructor() {
    super('Not permitted to open this estimate');
    this.name = 'EstimateForbiddenError';
  }
}

/** A 404: a stale or mistyped link, or an estimate deleted since — a missing page, not a failed request. */
export class EstimateNotFoundError extends Error {
  constructor() {
    super('No such estimate');
    this.name = 'EstimateNotFoundError';
  }
}

export function useEstimate(estimateId: string | undefined) {
  return useQuery({
    enabled: estimateId !== undefined,
    queryKey: [...ESTIMATES_KEY, 'detail', estimateId] as const,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/estimates/{estimateId}', {
        params: { path: { estimateId: estimateId! } },
      });
      if (response.status === 403) throw new EstimateForbiddenError();
      if (response.status === 404) throw new EstimateNotFoundError();
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/estimates/{id} failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}

export interface NewEstimate {
  customerId: string;
  propertyId: string;
  jobId: string | null;
}

/** Opens a draft (revision 1 of a new number). The caller navigates to it. */
export function useCreateEstimate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (estimate: NewEstimate) => {
      const { data, error, response } = await apiClient.POST('/api/v1/estimates', {
        body: { ...estimate, lines: [] },
      });
      if (!response.ok || !data) throw failure(error, response, 'POST /api/v1/estimates');
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: [...ESTIMATES_KEY, 'list'] }),
  });
}

/**
 * What the builder saves: the lines AND both notes, every time (M2-07).
 *
 * ⚠️ The API REPLACES the notes with the lines — a body that leaves a note out
 * clears it. So this type makes both required, and the builder's autosave and
 * its flush-on-unmount both send them; a caller that only meant to change a
 * line would otherwise erase the customer's message.
 */
export interface SaveDraft {
  lines: SaveLine[];
  customerMessage: string;
  internalNotes: string;
}

/**
 * Saves a draft — the autosave's one write. The saved estimate is put
 * straight into the detail cache (its totals and fresh line ids) and the lists
 * are invalidated, since their totals changed.
 */
export function useSaveEstimateDraft(estimateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (draft: SaveDraft) => {
      const { data, error, response } = await apiClient.PUT('/api/v1/estimates/{estimateId}', {
        params: { path: { estimateId } },
        body: {
          lines: draft.lines,
          customerMessage: draft.customerMessage,
          internalNotes: draft.internalNotes,
        },
      });
      if (!response.ok || !data) throw failure(error, response, 'PUT /api/v1/estimates/{id}');
      return data;
    },
    onSuccess: (saved) => {
      queryClient.setQueryData([...ESTIMATES_KEY, 'detail', estimateId], saved);
      void queryClient.invalidateQueries({ queryKey: [...ESTIMATES_KEY, 'list'] });
    },
    // A 409 means the estimate is no longer a draft — sent from another tab,
    // say. Re-read it, so the page shows the sent document instead of an
    // editor whose every keystroke is refused (found in review).
    onError: (error) => {
      if (error instanceof EstimateRefusedError) {
        void queryClient.invalidateQueries({ queryKey: [...ESTIMATES_KEY, 'detail', estimateId] });
      }
    },
  });
}

const PREVIEW_DEBOUNCE_MS = 400;

/**
 * What the lines on screen would come to if sent now — the builder's live
 * tax and total (M2-06). Debounced here, so a burst of typing is one request;
 * the previous answer stays on screen while the next one loads.
 *
 * `lines` is null while any line is invalid: there is nothing the server
 * could price, and the builder says why on the line itself.
 */
export function useEstimatePreview(estimateId: string, lines: SaveLine[] | null, enabled: boolean) {
  const key = lines === null ? null : JSON.stringify(lines);
  const [debounced, setDebounced] = useState(key);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(key), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [key]);

  const query = useQuery({
    enabled: enabled && debounced !== null,
    queryKey: [...ESTIMATES_KEY, 'preview', estimateId, debounced] as const,
    queryFn: async () => {
      const { data, error, response } = await apiClient.POST(
        '/api/v1/estimates/{estimateId}/preview',
        {
          params: { path: { estimateId } },
          body: { lines: JSON.parse(debounced!) as SaveLine[] },
        },
      );
      if (!response.ok || !data)
        throw failure(error, response, 'POST /api/v1/estimates/{id}/preview');
      return data;
    },
    placeholderData: keepPreviousData,
    retry: false,
    // Keyed by the whole line set, so every debounced edit is a new entry, and
    // only the latest is ever read (`keepPreviousData` shows the observer's
    // last result, not the cache's). Dropped soon after it stops being current.
    gcTime: 10_000,
  });

  // The answer is for the lines on screen only once the debounce has caught up
  // and the response is for that key — until then the builder says "updating".
  return {
    ...query,
    current: debounced === key && !query.isPlaceholderData && query.data !== undefined,
  };
}

/**
 * The office's acts on an estimate (M2-05's endpoints, given a screen by
 * M2-06): send, record the answer, revise, delete a draft. Each invalidates
 * every estimate query — a send supersedes the revision before it, which is a
 * different row the list is showing.
 */
export function useEstimateActions(estimateId: string) {
  const queryClient = useQueryClient();
  const onSuccess = () => void queryClient.invalidateQueries({ queryKey: ESTIMATES_KEY });
  const path = { params: { path: { estimateId } } };

  const send = useMutation({
    mutationFn: async (expiresOn: string | null) => {
      const { data, error, response } = await apiClient.POST(
        '/api/v1/estimates/{estimateId}/send',
        {
          ...path,
          body: { expiresOn },
        },
      );
      if (!response.ok || !data) throw failure(error, response, 'send');
      return data;
    },
    onSuccess,
  });

  const approve = useMutation({
    mutationFn: async () => {
      const { data, error, response } = await apiClient.POST(
        '/api/v1/estimates/{estimateId}/approve',
        path,
      );
      if (!response.ok || !data) throw failure(error, response, 'approve');
      return data;
    },
    onSuccess,
  });

  const decline = useMutation({
    mutationFn: async () => {
      const { data, error, response } = await apiClient.POST(
        '/api/v1/estimates/{estimateId}/decline',
        path,
      );
      if (!response.ok || !data) throw failure(error, response, 'decline');
      return data;
    },
    onSuccess,
  });

  const revise = useMutation({
    mutationFn: async () => {
      const { data, error, response } = await apiClient.POST(
        '/api/v1/estimates/{estimateId}/revise',
        path,
      );
      if (!response.ok || !data) throw failure(error, response, 'revise');
      return data;
    },
    onSuccess,
  });

  const remove = useMutation({
    mutationFn: async () => {
      const { error, response } = await apiClient.DELETE('/api/v1/estimates/{estimateId}', path);
      if (!response.ok) throw failure(error, response, 'delete');
    },
    onSuccess,
  });

  return { send, approve, decline, revise, remove };
}

export type ConvertedEstimate = components['schemas']['ConvertEstimateResponse'];

/**
 * How to convert (M2-09 follow-up). Omitted: the API's default — add to the
 * estimate's job, or a new job when it has none. `NewJob` is the way forward
 * for an estimate drafted on a finished job.
 */
export type ConvertMode = 'AddToJob' | 'NewJob';

/**
 * Converts an approved estimate into work on a job (M2-09) — a new
 * unscheduled job, or the job it was drafted on. `customers.manage` only.
 *
 * The answer always names the job, including `AlreadyConverted` (a double
 * click, or a colleague got there first): the API answers a second attempt
 * with the same job rather than a refusal, so the caller navigates either
 * way. A 409 is a refusal with its sentence (not approved, a finished or
 * invoiced job) in an {@link EstimateRefusedError}.
 *
 * With `NewJob` (M2-09 follow-up) the work goes onto a new job even though the
 * estimate names one — the job it was drafted on, which is finished. Both jobs
 * live under `jobs`: the new one gains its lines and source, and the old one's
 * estimate list now marks the estimate converted.
 *
 * ⚠️ Invalidates BOTH trees: the estimate now names its job and reads as
 * converted (detail and lists), and the job gained lines and a source
 * estimate — or exists for the first time, which the job lists, the board's
 * unscheduled rail and a customer's job history all show — plus the
 * property's page, whose recent jobs a new job joins.
 */
export function useConvertEstimate(estimateId: string, propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mode?: ConvertMode) => {
      const { data, error, response } = await apiClient.POST(
        '/api/v1/estimates/{estimateId}/convert',
        { params: { path: { estimateId } }, ...(mode ? { body: { mode } } : {}) },
      );
      if (!response.ok || !data) throw failure(error, response, 'convert');
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ESTIMATES_KEY });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['properties', propertyId] });
    },
    // A 409 means the estimate or its job changed since the page read it — the
    // job finished, or its invoice was voided, while the page sat open (M2-09
    // follow-up, found in review). Re-read the estimate, so `addToJobRefusal`
    // and the dialog offer what Convert will accept now instead of repeating
    // the refused act until the page is reloaded.
    onError: (error) => {
      if (error instanceof EstimateRefusedError) {
        void queryClient.invalidateQueries({ queryKey: [...ESTIMATES_KEY, 'detail', estimateId] });
      }
    },
  });
}

const CATALOG_SEARCH_DEBOUNCE_MS = 200;
const CATALOG_SEARCH_SIZE = 8;

/**
 * The builder's price-book search (M2-06): active items whose name matches,
 * a screenful at a time. A technician gets them without costs; nothing here
 * reads the cost anyway.
 */
export function useCatalogSearch(term: string) {
  const [debounced, setDebounced] = useState(term.trim());

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), CATALOG_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  const query = useQuery({
    enabled: debounced.length > 0,
    queryKey: ['catalog-items', 'search', debounced] as const,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/catalog-items', {
        params: { query: { q: debounced, pageSize: CATALOG_SEARCH_SIZE } },
      });
      if (!response.ok || !data)
        throw new Error(`GET /api/v1/catalog-items failed: ${response.status}`);
      return data.items;
    },
    placeholderData: keepPreviousData,
    retry: false,
  });

  // ⚠️ `keepPreviousData` keeps the LAST term's matches on screen while the
  // next search runs — right for the eye, wrong for Enter, which would add a
  // match for text that is no longer in the box. `fresh` says the matches are
  // for exactly what is typed now.
  const fresh = debounced === term.trim() && query.isSuccess && !query.isPlaceholderData;
  return { ...query, fresh };
}

/** The URL the customer opens: this app's own origin, `/e/{token}` (M2-07). */
export function customerLinkUrl(token: string): string {
  return `${window.location.origin}/e/${token}`;
}

/**
 * The customer's link (M2-07): copy — the live link, or a new one after a
 * revoke — and revoke. Both `customers.manage`. A copy the API refuses (a
 * draft, an expired or superseded estimate, one answered too long ago) comes
 * back as its sentence in an {@link EstimateRefusedError}.
 */
export function useEstimateLink(estimateId: string) {
  const path = { params: { path: { estimateId } } };

  const copy = useMutation({
    mutationFn: async () => {
      const { data, error, response } = await apiClient.POST(
        '/api/v1/estimates/{estimateId}/link',
        path,
      );
      if (!response.ok || !data) throw failure(error, response, 'copy link');
      return customerLinkUrl(data.token);
    },
  });

  const revoke = useMutation({
    mutationFn: async () => {
      const { error, response } = await apiClient.DELETE(
        '/api/v1/estimates/{estimateId}/link',
        path,
      );
      if (!response.ok) throw failure(error, response, 'revoke link');
    },
  });

  return { copy, revoke };
}

export type EstimateCustomerAnswer = components['schemas']['EstimateCustomerAnswerResponse'];

/**
 * The customer's drawn signature (M2-08), as a PNG blob — read through
 * `apiClient`, so it carries the bearer token an `<img src>` could not. The
 * caller turns it into an object URL (and revokes it).
 *
 * Immutable once written (the signed record refuses every UPDATE), so it is
 * never refetched on its own; an act on the estimate still invalidates it
 * with everything else under `estimates`.
 */
export function useEstimateSignature(estimateId: string, enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: [...ESTIMATES_KEY, 'signature', estimateId] as const,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/estimates/{estimateId}/signature', {
        params: { path: { estimateId } },
        parseAs: 'blob',
      });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/estimates/{id}/signature failed: ${response.status}`);
      }
      return data;
    },
    staleTime: Infinity,
    retry: false,
  });
}
